import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import '../styles/Applications.css';

const tArr = (t, key) => {
  const val = t(key, { returnObjects: true });
  return Array.isArray(val) ? val : [];
};

const tObj = (t, key) => {
  const val = t(key, { returnObjects: true });
  return val && typeof val === 'object' ? val : {};
};

const APP_TYPE_KEYS = [
  'projectManagement',
  'investigation',
  'knowledgeManagement',
  'workforceIntelligence',
  'enterpriseOperations',
  'middleware',
];

const Applications = () => {
  const { t } = useTranslation('applications');
  const { language } = useLanguage();

  const portfolioItems = tArr(t, 'portfolio.items');
  const whyUsItems = tArr(t, 'whyUs.items');
  const integrations = tArr(t, 'aiIntegration.integrations');

  return (
    <div className="applications-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonicalPath={`/${language}/applications`}
      />

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="hero-section applications-hero">
        <div className="hero-background">
          <div className="gradient-layer"></div>
          <div className="particle-layer"></div>
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-1"></div>
              <div className="tech-element tech-element-4"></div>
              <div className="tech-element tech-element-7"></div>
              <span className="app-hero-tag fade-in">
                <i className="fas fa-code"></i>
                {t('hero.tag')}
              </span>
              <h1 className="hero-quote fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
              <Link
                to={`/${language}/contact`}
                className="app-hero-cta fade-in"
                style={{ animationDelay: '0.35s' }}
              >
                {t('hero.cta')}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Intro ──────────────────────────────────────────────── */}
      <section className="app-section app-intro">
        <div className="container">
          <div className="app-intro-grid">
            <div className="app-stats-column">
              {['stat1', 'stat2', 'stat3'].map((key) => {
                const stat = tObj(t, `intro.${key}`);
                return (
                  <div key={key} className="app-stat-card fade-in">
                    {stat.value && <span className="app-stat-value">{stat.value}</span>}
                    <span className="app-stat-label">{stat.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="app-intro-text fade-in" style={{ animationDelay: '0.15s' }}>
              <span className="app-section-tag">{t('intro.tag')}</span>
              <h2 className="app-section-title">{t('intro.title')}</h2>
              <p>{t('intro.body1')}</p>
              <p>{t('intro.body2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Application Types ──────────────────────────────────── */}
      <section className="app-section app-types">
        <div className="container">
          <span className="app-section-tag fade-in">{t('appTypes.tag')}</span>
          <h2 className="app-section-title fade-in" style={{ animationDelay: '0.1s' }}>
            {t('appTypes.title')}
          </h2>
          <p className="app-section-subtitle fade-in" style={{ animationDelay: '0.15s' }}>
            {t('appTypes.subtitle')}
          </p>
          <div className="app-types-grid">
            {APP_TYPE_KEYS.map((key, idx) => {
              const item = tObj(t, `appTypes.items.${key}`);
              const features = tArr(t, `appTypes.items.${key}.features`);
              return (
                <div key={key} className="app-type-card fade-in" style={{ animationDelay: `${0.2 + idx * 0.1}s` }}>
                  <div>
                    <div className="app-type-inner-tag">{item.tag}</div>
                    <h3 className="app-type-title">{item.title}</h3>
                  </div>
                  <p className="app-type-desc">{item.description}</p>
                  <ul className="app-type-features">
                    {features.map((f, i) => (
                      <li key={i}>
                        <i className="fas fa-check"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Portfolio (commented out) ───────────────────────────
      <section className="app-section app-portfolio">
        <div className="container">
          <span className="app-section-tag fade-in">{t('portfolio.tag')}</span>
          <h2 className="app-section-title fade-in" style={{ animationDelay: '0.1s' }}>
            {t('portfolio.title')}
          </h2>
          <p className="app-section-subtitle fade-in" style={{ animationDelay: '0.15s' }}>
            {t('portfolio.subtitle')}
          </p>
          <div className="app-portfolio-grid">
            {portfolioItems.map((item, idx) => (
              <div key={idx} className="portfolio-card fade-in" style={{ animationDelay: `${0.2 + idx * 0.08}s` }}>
                <div className="portfolio-card-header">
                  <div className="portfolio-icon-wrap">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <div>
                    <div className="portfolio-client">{item.client}</div>
                    <div className="portfolio-project">{item.project}</div>
                  </div>
                </div>
                <span className="portfolio-industry-tag">{item.industry}</span>
                <p className="portfolio-highlight">{item.highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      ── end Portfolio ─────────────────────────────────────── */}

      {/* ── AI Integration ─────────────────────────────────────── */}
      <section className="app-section app-ai-integration">
        <div className="container">
          <div className="ai-integration-intro fade-in">
            <span className="app-section-tag">{t('aiIntegration.tag')}</span>
            <h2 className="app-section-title">{t('aiIntegration.title')}</h2>
            <p className="app-section-subtitle">{t('aiIntegration.subtitle')}</p>
          </div>

          {/* Use Case Banner */}
          <div className="ai-usecase-banner fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="ai-usecase-icon">
              <i className="fas fa-diagram-project"></i>
            </div>
            <div>
              <span className="ai-usecase-label">{t('aiIntegration.useCaseLabel')}</span>
              <h3 className="ai-usecase-title">{t('aiIntegration.useCaseTitle')}</h3>
              <p className="ai-usecase-body">{t('aiIntegration.useCaseBody')}</p>
            </div>
          </div>

          {/* Flow: PMS → AI */}
          <div className="ai-flow-bridge fade-in" style={{ animationDelay: '0.15s' }}>
            <div className="ai-flow-pms-node">
              <div className="ai-flow-pms-icon">
                <i className="fas fa-server"></i>
              </div>
              <div className="ai-flow-pms-label">
                <span>{t('aiIntegration.bridgeLabel')}</span>
                <span>{t('aiIntegration.bridgeValue')}</span>
              </div>
            </div>
            <div className="ai-flow-arrow">
              <i className="fas fa-angles-right"></i>
              <span>AI Integration</span>
            </div>
            <div className="ai-flow-pms-node ai-flow-pms-node--powered">
              <div className="ai-flow-pms-icon ai-flow-pms-icon--powered">
                <i className="fas fa-brain"></i>
              </div>
              <div className="ai-flow-pms-label">
                <span className="ai-flow-powered-tag">YOUR AI-POWERED</span>
                <span>PMS</span>
              </div>
            </div>
          </div>

          {/* Compact Timeline */}
          <div className="ai-timeline fade-in" style={{ animationDelay: '0.2s' }}>
            {integrations.map((item, idx) => (
              <div key={item.key || idx} className="ai-timeline-step">
                <div className="ai-timeline-node">
                  <div className="ai-timeline-badge">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  {idx < integrations.length - 1 && (
                    <div className="ai-timeline-connector"></div>
                  )}
                </div>
                <div className="ai-timeline-body">
                  <span className="ai-timeline-tag">{item.tag}</span>
                  <h4 className="ai-timeline-title">{item.title}</h4>
                  <p className="ai-timeline-summary">{item.summary}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div className="ai-integration-note fade-in" style={{ animationDelay: '0.4s' }}>
            <i className="fas fa-lightbulb"></i>
            <p>{t('aiIntegration.note')}</p>
          </div>
        </div>
      </section>

      {/* ── Platform CTA ───────────────────────────────────────── */}
      <section className="app-section app-platform-cta">
        <div className="container">
          <div className="app-platform-cta-inner fade-in">
            <div className="app-platform-cta-text">
              <span className="app-section-tag">{t('platformCta.tag')}</span>
              <h2 className="app-section-title">{t('platformCta.title')}</h2>
              <p>{t('platformCta.body')}</p>
              <div className="app-platform-link-note">
                <i className="fas fa-link"></i>
                {t('platformCta.linkNote')}
              </div>
            </div>
            <div>
              <Link
                to={`/${language}/platform#deployment`}
                className="app-platform-cta-btn"
              >
                {t('platformCta.button')}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Us (commented out) ─────────────────────────────
      <section className="app-section app-why-us">
        <div className="container">
          <span className="app-section-tag fade-in">{t('whyUs.tag')}</span>
          <h2 className="app-section-title fade-in" style={{ animationDelay: '0.1s' }}>
            {t('whyUs.title')}
          </h2>
          <div className="app-why-us-grid" style={{ marginTop: '2rem' }}>
            {whyUsItems.map((item, idx) => (
              <div key={idx} className="why-us-card fade-in" style={{ animationDelay: `${0.15 + idx * 0.1}s` }}>
                <div className="why-us-icon-wrap">
                  <i className={`fas ${item.icon}`}></i>
                </div>
                <div>
                  {item.value && <span className="why-us-value">{item.value}</span>}
                  <h4 className="why-us-title">{item.title}</h4>
                  <p className="why-us-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      ── end Why Us ───────────────────────────────────────── */}

      {/* ── Contact CTA ────────────────────────────────────────── */}
      <section className="app-section app-contact-cta">
        <div className="container">
          <div className="app-contact-inner">
            <span className="app-section-tag fade-in">{t('contact.tag')}</span>
            <h2 className="app-section-title fade-in" style={{ animationDelay: '0.1s' }}>
              {t('contact.title')}
            </h2>
            <p className="fade-in" style={{ animationDelay: '0.15s' }}>
              {t('contact.body')}
            </p>
            <div className="app-contact-actions fade-in" style={{ animationDelay: '0.2s' }}>
              <Link to={`/${language}/contact`} className="app-contact-btn-primary">
                {t('contact.button')}
                <i className="fas fa-arrow-right"></i>
              </Link>
              <Link to={`/${language}/capabilities`} className="app-contact-btn-secondary">
                {t('contact.secondaryButton')}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Applications;
